/*
  OL Core v1.0 — unified logic for Order Limits widgets (table + emoji)
  ---------------------------------------------------------------------
  • Single source of truth for:
    - Tag → allowances (global/skin/makeup)
    - Product → category detection
    - Cart counting, overage checks, and state emission
    - Safe header cart-count syncing & nav label text (no duplicates)
  • Emits: `document` event "ol:state" with full computed state
  • Exposes API on `window.OL`

  NOTE: Fixes the FT2 global cap (now 10), so TOTAL shows e.g. "4/10" instead of "4/—".
*/

(function(){
  'use strict';

  /* ====== Utilities ====== */
  function slug(s){ return (s||"").toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,''); }
  const qs=(s,r=document)=>r.querySelector(s);
  const qsa=(s,r=document)=>Array.prototype.slice.call(r.querySelectorAll(s));

  /* ====== Shared constants ====== */
  const MATRIX={
    skincare:[
      ["Cleansing / Toning",4,["cleanser","toner","toning","micellar"]],
      ["Eye Care",3,["eye care","eye cream","eye gel","eye"]],
      ["Treatment Oils",2,["treatment oil","facial oil","oil"]],
      ["Serums & Masks",3,["serum","mask","ampoule","booster"]],
      ["Moisturizers",4,["moisturizer","moisturiser","cream","lotion","emulsion","balm"]],
      ["Face Specifics",4,["spot","exfoliator","peel","scrub"]],
      ["Treatment Frag",2,["fragrance","frag","aroma"]],
      ["Body Care",4,["body","hand","foot"]],
      ["Suncare",2,[
        "spf","sunscreen","sun cream","suncream","sun block","sunblock",
        "suncare","sun care","after sun","aftersun","after-sun","uv","uva","uvb"
      ]]
    ],
    makeup:[
      ["Foundation / BB",2,["foundation","bb","cc","tint"]],
      ["Lipstick / Lip Gloss",3,["lipstick","lip gloss","gloss","lip oil","lip stain"]],
      ["Eye Colours",2,["eyeshadow","shadow","palette","pigment"]],
      ["Blush / Bronzing",2,["blush","bronze","bronzer","cheek"]],
      ["Mascara / Pencils",2,["mascara","pencil","liner","kohl","brow"]],
      ["Concealers",2,["conceal","concealer"]],
      ["Brushes",2,["brush","applicator"]]
    ]
  };

  /*
    Allowances map (global caps align with section sums)
      FT0/PT0 → g=0,  m=0, s=0
      FT1     → g=5,  m=2, s=3
      FT2     → g=10, m=4, s=6   
      FT3     → g=15, m=6, s=9
      PT1     → g=3,  m=1, s=2
      PT2     → g=5,  m=2, s=3
      PT3     → g=8,  m=3, s=5
      IN1     → g=10,  m=3, s=7
  */


  const ALLOW={
    FT0:{g:0,m:0,s:0},
    FT1:{g:5,m:2,s:3},
    FT2:{g:10,m:4,s:6},
    FT3:{g:15,m:6,s:9},
    PT0:{g:0,m:0,s:0},
    PT1:{g:3,m:1,s:2},
    PT2:{g:5,m:2,s:3},
    PT3:{g:8,m:3,s:5},
    IN1:{g:10,m:3,s:7}
    


  };

  const PROPERTY_HINTS=["order type","order_type","form type","form_type","type","group","limit group","limit-group"];

  /* Build keys + max map */
  const KEYS={ skincare:[], makeup:[] };
  const MAX={};
  MATRIX.skincare.forEach(r=>{ const k=slug(r[0]); KEYS.skincare.push(k); MAX[k]=r[1]; });
  MATRIX.makeup.forEach(r=>{ const k=slug(r[0]); KEYS.makeup.push(k); MAX[k]=r[1]; });

  /* tolerant matcher: spaces/hyphens/underscores/slashes */
  function matchBy(text, rows){
    text = (text||'').toLowerCase();
    const tight = text.replace(/[\s\-_\/]+/g,'');
    for(let i=0;i<rows.length;i++){
      const key = slug(rows[i][0]), hints = rows[i][2] || [];
      for(let j=0;j<hints.length;j++){
        const needle = (hints[j]||'').toLowerCase();
        const needleTight = needle.replace(/[\s\-_\/]+/g,'');
        if (text.includes(needle) || tight.includes(needleTight)) return key;
      }
    }
    return null;
  }

function detectGroup(item){
  // 1) Line-item properties → explicit mapping
  const props = item && item.properties;
  if (props){
    for (const k in props){
      if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
      const val = (props[k] || '').toString().trim();
      if (!val) continue;

      // allow "moisturizers" / "moisturizers" etc
      for (const row in MAX){
        const nice = row.replace(/-/g,' ');
        if (val.toLowerCase() === nice || slug(val) === row) return row;
      }
      // properties like "order type", "limit group", etc.
      if (PROPERTY_HINTS.some(h => k.toLowerCase().includes(h))){
        const g = slug(val);
        if (g in MAX) return g;
      }
    }
  }

  // 2) Exact product_type → row name (preferred)
  const pt = (item && item.product_type || '').trim();
  if (pt){
    const ptSlug = slug(pt);
    if (ptSlug in MAX) return ptSlug;                           // e.g., "Eye Colours" → "eye-colours"
    for (const row in MAX){                                     // human readable equality
      const nice = row.replace(/-/g,' ');
      if (pt.toLowerCase() === nice) return row;
    }
  }

  // 3) Fuzzy pass — prefer MAKE-UP before SKINCARE to avoid "eye" false positives
  const gByPT =
    matchBy(item && item.product_type, MATRIX.makeup) ||
    matchBy(item && item.product_type, MATRIX.skincare);
  if (gByPT) return gByPT;

  const gByTitle =
    matchBy(item && item.title, MATRIX.makeup) ||
    matchBy(item && item.title, MATRIX.skincare);
  if (gByTitle) return gByTitle;

  // 4) No match
  return null;
}

    
  function getAllow(profileTag){
  const tag = (profileTag || '').trim();
  if (tag in ALLOW) return ALLOW[tag];
  return { g:0, m:0, s:0 }; // fail closed
}

  /* ====== SVG helpers (for headers) ====== */
  function svgOK(){
    return '<svg class="ol-icon" viewBox="0 0 30 30" aria-hidden="true"><circle cx="15" cy="15" r="14" fill="var(--ol-ok)"></circle><path fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.4" d="M9 15l4 4 8-8"></path></svg>';
  }
  function svgWARN(){
    return '<svg class="ol-icon" viewBox="0 0 30 30" aria-hidden="true"><path d="M12.4 2.563L.377 24.518A3.023 3.023 0 0 0 3.006 29h23.987a3.023 3.023 0 0 0 2.632-4.477L17.66 2.569a2.992 2.992 0 0 0-5.26-.006z" fill="var(--ol-warn)" opacity=".25"></path><path fill="none" stroke="var(--ol-warn)" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19v-9"></path><circle cx="15" cy="23.5" r="1.5" fill="var(--ol-warn)"></circle></svg>';
  }
  function svgERR(){
    return '<svg class="ol-icon" viewBox="0 0 30 30" aria-hidden="true"><circle cx="15" cy="15" r="14" fill="var(--ol-err)"></circle><path fill="none" stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2.4" d="M15 8v9"></path><circle cx="15" cy="22.5" r="1.6" fill="#fff"></circle></svg>';
  }



    // Read a CSS variable (string or quoted JSON)
  function _cssVar(name, fallback){
    try{
      const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      return (v || fallback || '').replace(/^"|"$/g,'');
    }catch(_){ return fallback || ''; }
  }

  /* Donut for TOTAL — shows USED progress (fills as cart fills) */
  function svgTOTAL_DONUT(total, limit, status){
    if (limit == null || limit <= 0) return svgWARN(); // no global cap → fallback icon

    const size   = parseFloat(_cssVar('--ol-donut-size','20'));   // px
    const stroke = parseFloat(_cssVar('--ol-donut-stroke','3'));  // px
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;

    const used = Math.max(0, Math.min(total, limit));
    const pct = used / limit;
    const dashOffset = c * (1 - pct);

    const track = _cssVar('--ol-donut-track', '#e5e7eb');
    const colMap = {
      ok:   _cssVar('--ol-donut-ok',   '#22c55e'),
      warn: _cssVar('--ol-donut-warn', '#f59e0b'),
      err:  _cssVar('--ol-donut-err',  '#ef4444')
    };
    const strokeCol = colMap[status] || colMap.warn;

    return `
    <svg class="ol-icon" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" aria-hidden="true">
      <g transform="rotate(-90 ${size/2} ${size/2})">
        <circle cx="${size/2}" cy="${size/2}" r="${r}" fill="none"
                stroke="${track}" stroke-width="${stroke}"></circle>
        <circle cx="${size/2}" cy="${size/2}" r="${r}" fill="none"
                stroke="${strokeCol}" stroke-width="${stroke}" stroke-linecap="round"
                stroke-dasharray="${c}" stroke-dashoffset="${dashOffset}"
                style="transition:stroke-dashoffset .28s ease"></circle>
      </g>
    </svg>`;
  }



  /* ====== Counters & state ====== */
  function blankCounters(){ const o={}; [...KEYS.skincare, ...KEYS.makeup].forEach(k=>o[k]=0); return o; }

  function computeStateFromCart(cart, profileTag){
    const allow = getAllow(profileTag);
    const counters = blankCounters();

    (cart.items||[]).forEach(item=>{
      const qty=item.quantity||0; if(!qty) return;
      const key=detectGroup(item);
      if(key && (key in counters)) counters[key]+=qty;
    });

    const sum = (arr)=>arr.reduce((n,k)=>n+(counters[k]||0),0);
    const totS = sum(KEYS.skincare);
    const totM = sum(KEYS.makeup);
    const totG = totS + totM;

    const subOverS = KEYS.skincare.some(k=> (counters[k]||0)>(MAX[k]||0) && (MAX[k]||0)>0 );
    const subOverM = KEYS.makeup.some(k=> (counters[k]||0)>(MAX[k]||0) && (MAX[k]||0)>0 );

    const overByTotalS = (typeof allow.s==='number' && allow.s>=0 && totS>allow.s);
    const overByTotalM = (typeof allow.m==='number' && allow.m>=0 && totM>allow.m);
    const overByTotalG = (typeof allow.g==='number' && allow.g>=0 && totG>allow.g);

    const overS = subOverS || overByTotalS;
    const overM = subOverM || overByTotalM;
    const fullS=(typeof allow.s==='number' && allow.s>0 && totS===allow.s && !overS);
    const fullM=(typeof allow.m==='number' && allow.m>0 && totM===allow.m && !overM);

    let gIcon = 'warn'; // default warn if no hard cap
    if(overS || overM) gIcon='err';
    else if(allow.g==null) gIcon='warn';
    else if(totG>allow.g) gIcon='err';
    else if(totG===allow.g) gIcon='ok';
    else gIcon='warn';

    // sub-row overages
    const overages = [];
    Object.keys(counters).forEach(k=>{
      const c=counters[k]||0, m=MAX[k]||0;
      if(m>0 && c>m){ overages.push({ key:k, name:k.replace(/-/g,' '), count:c, max:m, over:c-m }); }
    });

    // section-level overages (totals exceeding s/m caps)
    const sectionOverages = [];
    const sOverBy = (overByTotalS ? (totS - allow.s) : 0);
    const mOverBy = (overByTotalM ? (totM - allow.m) : 0);
    if (sOverBy>0) sectionOverages.push({ section:'SKINCARE', total:totS, allow:allow.s, over:sOverBy });
    if (mOverBy>0) sectionOverages.push({ section:'MAKE-UP', total:totM, allow:allow.m, over:mOverBy });

    const globalOverBy = (overByTotalG ? (totG - allow.g) : 0);

    return {
      profileTag,
      allow,
      counters,
      keys: KEYS,
      max: MAX,
      totals:{ s:totS, m:totM, g:totG },
      flags:{ subOverS, subOverM, overByTotalS, overByTotalM, overByTotalG, overS, overM, fullS, fullM, globalIcon:gIcon },
      overages,              // sub-row overages
      sectionOverages,       // section totals overage
      globalOverBy,          // amount over global cap (0 if not over or g is null)
      cart
    };
  }

  /* ====== Header cart-count sync ====== */
  function syncHeaderAfterServerChange(cart){
    try{
      const count = (cart && typeof cart.item_count === 'number') ? cart.item_count : 0;
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const base = bubble.dataset.ariaBase || (
          bubble.dataset.ariaBase = (bubble.getAttribute('aria-label') || 'Open cart')
            .replace(/\s*(\(\d+\)\s*)+$/,'')
            .trim()
        );
        bubble.setAttribute('aria-label', `${base} (${count})`);
        bubble.setAttribute('data-cart-count', String(count));
      }
      document.querySelectorAll('.cart-count-bubble').forEach(bub=>{
        const vHidden = bub.querySelector('.visually-hidden');
        const num = bub.querySelector('[aria-hidden="true"]') || bub.querySelector('.text');
        if (num) num.textContent = String(count);
        if (vHidden) vHidden.textContent = count === 1 ? '1 item' : count + ' items';
        bub.hidden = (count === 0);
      });
      document.querySelectorAll('[data-cart-count], .header__cart-count, .site-header__cart-count, .cart-count, .js-cart-count').forEach(el=>{
        if (el.id !== 'cart-icon-bubble') el.textContent = String(count);
        if ('hidden' in el) el.hidden = (count === 0);
      });
      document.dispatchEvent(new CustomEvent('ol:cart:count', { detail:{ count, cart }}));
    }catch(_e){}
  }

  /* ====== NAV 'YOUR ALLOCATION' LABEL — dedupe + reuse theme label ====== */

function _findAllocLinks(){
  const set = new Set();

  // Explicit hooks if present (unchanged)
  const byId = document.getElementById('nav-alloc-link');
  if (byId) set.add(byId);
  const labelId = document.getElementById('nav-alloc-label');
  if (labelId) { const a = labelId.closest('a'); if (a) set.add(a); }

  // URL-based matches (unchanged) — deliberately NOT matching /cart
  document.querySelectorAll('a[href*="/your-allocation"], a[href*="/pages/your-allocation"]').forEach(a=>set.add(a));

  // Visible text fallback — add "YOUR CART"
  Array.from(document.querySelectorAll('a')).forEach(a=>{
    const t=(a.textContent || '').trim().toUpperCase();
    if (
      t === 'YOUR ALLOCATION' || t.startsWith('YOUR ALLOCATION') ||
      t === 'YOUR CART'       || t.startsWith('YOUR CART')
    ) set.add(a);
  });

  return Array.from(set);
}


  // Convert the theme’s title span into our managed label, and ensure only one label exists
  function _ensureLabelSpan(linkEl){
    // Base label text (cached once on the anchor)
    let base =
      linkEl.getAttribute('data-ol-base') ||
      linkEl.querySelector('.js-nav-alloc-label')?.dataset.base ||
      linkEl.querySelector('.header__active-menu-item')?.textContent?.trim() ||
      (linkEl.textContent || 'YOUR ALLOCATION').trim() ||
      'YOUR ALLOCATION';
    linkEl.setAttribute('data-ol-base', base);

    // Prefer using the theme’s existing label span (desktop)
    const themeLabel = linkEl.querySelector('.header__active-menu-item');
    if (themeLabel) {
      // Remove any previously injected duplicates
      linkEl.querySelectorAll('.js-nav-alloc-label').forEach(el => {
        if (el !== themeLabel) el.remove();
      });
      themeLabel.classList.add('js-nav-alloc-label');
      if (!themeLabel.dataset.base) themeLabel.dataset.base = base;
      themeLabel.style.whiteSpace = 'nowrap';
      return themeLabel;
    }

    // If an injected label exists, use it
    let label = linkEl.querySelector('.js-nav-alloc-label');
    if (label) {
      if (!label.dataset.base) label.dataset.base = base;
      label.style.whiteSpace = 'nowrap';
      return label;
    }

    // Create a new label; hide raw base text siblings to prevent doubles
    label = document.createElement('span');
    label.className = 'js-nav-alloc-label';
    label.dataset.base = base;
    label.style.whiteSpace = 'nowrap';

    // Remove non-empty text nodes to avoid showing base twice
    Array.from(linkEl.childNodes).forEach(node=>{
      if (node.nodeType === 3 && node.textContent.trim().length) linkEl.removeChild(node);
    });

    // Hide element children that are *just* the base text
    Array.from(linkEl.children).forEach(el=>{
      if (el !== label) {
        const txt = (el.textContent || '').trim();
        if (txt && txt.toUpperCase() === base.toUpperCase()) {
          el.setAttribute('aria-hidden','true');
          el.style.display = 'none';
        }
      }
    });

    linkEl.appendChild(label);
    return label;
  }

function updateNavAllocationLabel(profileTag, total, globalAllow){
  const allowStr = (globalAllow == null ? '—' : globalAllow);
  const txt = `YOUR CART (${total}/${allowStr})`;

  _findAllocLinks().forEach(linkEl=>{
    const label = _ensureLabelSpan(linkEl);
    if (label.textContent !== txt) label.textContent = txt;

    linkEl.setAttribute('href','/cart');  // go to cart
    linkEl.removeAttribute('target');     // stay in same window
    linkEl.setAttribute('aria-label', txt);
  });
}

  /* ====== Fetch/refresh & event bus ====== */
  let _profileTag='';
  let _boundNetHooks=false;
  let _refreshTimer=null;
  let _inited=false;

  function emitState(state){
    try{ document.dispatchEvent(new CustomEvent('ol:state', { detail: state })); }catch(_){}
  }

  function refresh(){
    return fetch('/cart.js?ol=1',{credentials:'same-origin'})
      .then(r=>r.json())
      .then(cart=>{
        const st=computeStateFromCart(cart,_profileTag);
        emitState(st);
        syncHeaderAfterServerChange(cart);
        updateNavAllocationLabel(st.profileTag, st.totals.g, st.allow.g);
        return st;
      })
      .catch(()=>{
        const st=computeStateFromCart({items:[], item_count:0}, _profileTag);
        emitState(st);
        // Still try to update the label (will show "—" if g is null)
        updateNavAllocationLabel(st.profileTag, st.totals.g, st.allow.g);
        return st;
      });
  }

  function queueRefresh(delay=200){ clearTimeout(_refreshTimer); _refreshTimer=setTimeout(refresh, delay); }

  function bindNetworkHooks(){
    if(_boundNetHooks) return; _boundNetHooks=true;
    const _fetch=window.fetch;
    window.fetch=function(){
      const url=arguments[0]; const p=_fetch.apply(this,arguments);
      try{
        const u=typeof url==='string'?url:(url&&url.url)||'';
        if(u.indexOf('/cart')!==-1 && !/[?&]ol=1\b/.test(u)){ p.then(()=>queueRefresh(120)); }
      }catch(_){}
      return p;
    };
    const X=window.XMLHttpRequest; if(X){
      const open=X.prototype.open, send=X.prototype.send;
      X.prototype.open=function(m,u){ this.__isCart = typeof u==='string' && /\b\/cart(\/|\.|$)/.test(u); return open.apply(this,arguments); };
      X.prototype.send=function(){ if(this.__isCart){ this.addEventListener('load', ()=>queueRefresh(120)); } return send.apply(this,arguments); };
    }
  }

  /* ====== Public API (no FixIt exported) ====== */
  window.OL = {
    init(opts){
      if(_inited){
        if(opts&&opts.profileTag) _profileTag=(opts.profileTag||'').trim();
        return refresh();
      }
      _inited=true;
      _profileTag=(opts&&opts.profileTag||'').trim();
      bindNetworkHooks();
      const st = refresh();
      try{ document.dispatchEvent(new CustomEvent('ol:ready')); }catch(_){}
      return st;
    },
    refresh, queueRefresh,
    onState(cb){ const h=(e)=>cb(e.detail); document.addEventListener('ol:state', h); return ()=>document.removeEventListener('ol:state', h); },
    detectGroup, getAllow, computeStateFromCart,
    constants:{ MATRIX, ALLOW, KEYS, MAX },
    svg:{ ok:svgOK, warn:svgWARN, err:svgERR, donut:svgTOTAL_DONUT },
    cssVar: _cssVar,
    syncHeaderAfterServerChange, updateNavAllocationLabel,
  };
})();

/* ================================================================
   === GLUE: TABLE WIDGET (auto-binds to #ol-feed if present) ===
   ================================================================ */
document.addEventListener('DOMContentLoaded', function(){
  const wrap=document.getElementById('ol-feed'); if(!wrap) return;
  const profileTag=(wrap.getAttribute('data-profile')||window.OL_PROFILE||'').trim();

  function renderSubList(ul, rows, type){
    if(!ul) return;
    ul.innerHTML=rows.map(r=>{
      const name=r[0], max=r[1], key=(name||'').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,'');
      return `<li class="ol-subitem" data-type="${type}" data-key="${key}" data-max="${max}">
        <span class="ol-lefticon ol-lefticon--ghost" aria-hidden="true"></span>
        <div class="ol-subname">${name}</div>
        <div class="ol-subcount">(<span class="js-count">0</span>/${max})</div>
        <div class="ol-substatus">
          <span class="js-err" hidden aria-hidden="true"></span>
        </div>
      </li>`;
    }).join('');
  }
  renderSubList(document.getElementById('ol-skin'), OL.constants.MATRIX.skincare, 'SKINCARE');
  renderSubList(document.getElementById('ol-makeup'), OL.constants.MATRIX.makeup, 'MAKE-UP');

  // Smooth details animation
  Array.prototype.slice.call(document.querySelectorAll('.ol-sec')).forEach(sec=>{
    const summary=sec.querySelector('summary'); const panel=sec.querySelector('.ol-panel');
    if(!summary||!panel) return;
    summary.addEventListener('click',function(e){
      e.preventDefault();
      if(sec.hasAttribute('open')){
        panel.style.height=panel.scrollHeight+'px'; panel.offsetHeight; panel.style.transition='height .28s ease'; panel.style.height='0px';
        panel.addEventListener('transitionend',function h(){panel.removeEventListener('transitionend',h);sec.removeAttribute('open');panel.style.transition='';},{once:true});
      }else{
        sec.setAttribute('open',''); panel.style.height='0px'; panel.offsetHeight; panel.style.transition='height .28s ease'; panel.style.height=panel.scrollHeight+'px';
        panel.addEventListener('transitionend',function h(){panel.removeEventListener('transitionend',h);panel.style.transition='';panel.style.height='auto';},{once:true});
      }
    });
  });

  // Paint on state
  OL.onState(function(st){
    const allow=st.allow;
    const allowG=(allow.g==null?'—':allow.g);
    const set=(sel,val)=>{ const el=document.querySelector(sel); if(el) el.textContent=val; };
    set('.js-allow-g', allowG); set('.js-allow-s', allow.s); set('.js-allow-m', allow.m);

    set('.js-tot-s', st.totals.s); set('.js-tot-m', st.totals.m); set('.js-tot-g', st.totals.g);

    const leftS = document.querySelector('.js-left-s');
    const leftM = document.querySelector('.js-left-m');
    const leftG = document.querySelector('.js-left-g');

    // Helper to render a section header icon/donut
    function paintSection(leftEl, total, limit, isOver, isFull){
      if (!leftEl) return;
      const secMode = OL.cssVar('--ol-section-mode','icon');      // 'icon' | 'donut'
      const tickOnComplete = OL.cssVar('--ol-section-complete-tick','true') === 'true';
      const status = isOver ? 'err' : (isFull ? 'ok' : 'warn');

  if (secMode === 'donut' && limit > 0) {
    if (isFull && tickOnComplete) {
      // Use your standard OK mask icon (no inline SVG)
      leftEl.classList.remove('has-inline');   // allow ::before mask to show
      leftEl.innerHTML = '';
    } else {
      // Inline donut for section progress
      leftEl.classList.add('has-inline');      // show inline SVG, hide mask
      leftEl.innerHTML = OL.svg.donut(total, limit, status);
    }
  } else {
    // Icon mode: defer to mask-based icons controlled by row classes
    leftEl.classList.remove('has-inline');
    leftEl.innerHTML = '';
  }
}

paintSection(
  leftS,
  st.totals.s, st.allow.s,
  st.flags.overS, st.flags.fullS
);
paintSection(
  leftM,
  st.totals.m, st.allow.m,
  st.flags.overM, st.flags.fullM
);


if (leftG) {
  const mode = OL.cssVar('--ol-total-mode','icon'); // 'icon' | 'donut'
  const hasCap = st.allow.g != null && st.allow.g > 0;
  const isComplete = hasCap && st.totals.g === st.allow.g && st.flags.globalIcon !== 'err';
  const showTickOnComplete = OL.cssVar('--ol-total-complete-tick','true') === 'true';

  if (mode === 'donut' && hasCap) {
    if (isComplete && showTickOnComplete) {
      // ✅ Use the standard OK mask icon: no inline SVG
      leftG.classList.remove('has-inline'); // let ::before mask show
      leftG.innerHTML = '';                 // nothing inline
      // headerLogic will set the `is-ok` class on the row, which makes the mask appear
    } else {
      // normal donut
      leftG.classList.add('has-inline');    // allow inline SVG
      leftG.innerHTML = OL.svg.donut(st.totals.g, st.allow.g, st.flags.globalIcon);
    }
  } else {
    // icon mode (always use mask icons)
    leftG.classList.remove('has-inline');
    leftG.innerHTML = ''; // leave to mask
  }
}



  });

  // Live hooks
  document.addEventListener('cart:refresh', ()=>OL.queueRefresh(80));
  document.addEventListener('ajaxCart:afterUpdate', ()=>OL.queueRefresh(80));
  document.addEventListener('change', e=>{
    const n=(e.target&&e.target.name||'').toLowerCase();
    if(n.includes('quantity')||n==='updates[]'||n.includes('updates[')) OL.queueRefresh(200);
  });

  // Boot
  OL.init({ profileTag });

// After OL.init({ profileTag });
if (window.Shopify && Shopify.designMode) {
  ['shopify:section:load','shopify:section:select','shopify:section:reorder',
   'shopify:inspector:activate','shopify:inspector:deactivate']
  .forEach(evt => document.addEventListener(evt, () => OL.queueRefresh(0)));
}

});

/* ===============================================================
   === GLUE: EMOJI WIDGET (keeps your HTML/CSS/templates) ===
   =============================================================== */
document.addEventListener('DOMContentLoaded', function(){
  const fab = document.getElementById('fab'); if(!fab) return;
  const face = document.getElementById('fabFace');
  const msgEl = document.getElementById('fabCartInfo');
  const fabBtn = document.getElementById('fabBtn');
  const closeBtn = document.getElementById('fabClose');
  const backdrop = document.getElementById('fabBackdrop');
  const pop = document.getElementById('fabPopover');
  let lastState=null; let animTimer=null;

  const svgFromTemplate=(id)=>{ const t=document.getElementById(id); return t?t.content.firstElementChild.cloneNode(true):null; };
  function setFace(state){
    if(state!==lastState){
      lastState=state; face.classList.remove('play'); face.innerHTML='';
      const map={ ok:'tmpl-emoji-ok', warn:'tmpl-emoji-warn', err:'tmpl-emoji-error' };
      const svg=svgFromTemplate(map[state]||map.warn); if(svg) face.appendChild(svg);
      requestAnimationFrame(()=>face.classList.add('play'));
    }
  }
  function replayAnim(){ face.classList.remove('play'); requestAnimationFrame(()=>face.classList.add('play')); }

  function buildMessage(st){
  

    const { allow, totals, profileTag } = st;
    const total=totals.g, limit=allow.g, s=allow.s, m=allow.m, remaining=(limit==null?null:(limit-total));
    const name=(window.OL_CUSTOMER && window.OL_CUSTOMER.first_name)||'';

    if(limit===0){
      return `Hi ${name||'there'}, your code: <strong>${profileTag||''}</strong>.<br><strong>You’re not yet eligible for an allocation.</strong>`;
    }

    // Are we over anywhere?
    const hasOver = st.flags.overS || st.flags.overM || (limit!=null && total>limit) || st.overages.length>0 || (st.sectionOverages && st.sectionOverages.length>0);

    if(hasOver){
      // Combine: sub-row overages + section-level overages
      const subBits = st.overages.map(o=>`${o.name} ${o.count}/${o.max} (+${o.over})`);
      const secBits = (st.sectionOverages||[]).map(o=>`${o.section} ${o.total}/${o.allow} (+${o.over})`);
      const overBits = [...secBits, ...subBits].join(' • ');
      const overLine = overBits ? `<br><strong>Over in:</strong> ${overBits}` : '';

      // Reduction guidance: global + per-section when applicable
      const totalOver = (limit!=null && total>limit) ? (total-limit) : 0;
      const reduceParts = [];
      (st.sectionOverages||[]).forEach(o=>{
        reduceParts.push(`Reduce <strong>${o.section}</strong> by <strong>${o.over}</strong>`);
      });
      if (totalOver>0) reduceParts.push(`Remove <strong>${totalOver}</strong> item(s) to proceed`);
      const reduceLine = reduceParts.length ? `<br>${reduceParts.join(' • ')}.` : '';

      const remLine = (limit!=null) ? `<br>Cart: <strong>${total}/${limit}</strong> (<strong>${Math.max(0,remaining)}</strong> remaining).` : '';

      return `Hi ${name||'there'}, your code: <strong>${profileTag||''}</strong>.${overLine}${reduceLine}${remLine}`;
    }

    if(limit==null){
      return `Hi ${name||'there'}, your code: <strong>${profileTag||''}</strong>.<br>
              No fixed total cap. Section caps: <strong>${s}</strong> Skincare, <strong>${m}</strong> Makeup.<br>
              Cart: <strong>${total}</strong>.`;
    }
    if(total<limit){
      return `Hi ${name||'there'}, your code: <strong>${profileTag||''}</strong>.<br>
              You can order up to <strong>${limit}</strong> products <br>(<strong>${s}</strong> Skincare, <strong>${m}</strong> Makeup).<br>
              Cart: <strong>${total}/${limit}<br></strong> (<strong>${remaining}</strong> remaining).`;
    }
    return `Hi ${name||'there'}, your code: <strong>${profileTag||''}</strong>.<br>
            <strong>You’ve reached your allocation.</strong><br>
            Cart: <strong>${total}/${limit}</strong> (0 remaining).`;
  }

function stateToFace(st){

  const total = st.totals.g, limit = st.allow.g;
  if (limit === 0) return 'err';
  if (st.flags.overS || st.flags.overM) return 'err';
  if (limit == null) return 'warn';
  if (total > limit) return 'err';
  if (total < limit) return 'warn';
  return 'ok';
}




  function openFab() {
  fab.classList.add('fab--active');
  fabBtn && fabBtn.setAttribute('aria-expanded', 'true');
  pop && pop.setAttribute('aria-hidden', 'false');
}

function closeFab() {
  fab.classList.remove('fab--active');
  fabBtn && fabBtn.setAttribute('aria-expanded', 'false');
  pop && pop.setAttribute('aria-hidden', 'true');

  // 👇 ensures shadow reappears after closing
  if (fabBtn) fabBtn.blur();
}

  fabBtn && fabBtn.addEventListener('click',e=>{ e.stopPropagation(); fab.classList.contains('fab--active')?closeFab():openFab(); replayAnim(); });
  closeBtn && closeBtn.addEventListener('click',e=>{ e.stopPropagation(); closeFab(); });
  backdrop && backdrop.addEventListener('click', closeFab);
  fab.addEventListener('click',e=>{ if(e.target===fab) closeFab(); });
  document.addEventListener('keydown',e=>{ if(e.key==='Escape'&&fab.classList.contains('fab--active')) closeFab(); });

  // Subscribe to OL state
const unsubscribe = OL.onState(function(st){
  setFace(stateToFace(st));
  msgEl.innerHTML = buildMessage(st);

  // ✅ CHECKOUT VISIBILITY — single source of truth
const btn = document.getElementById('fabCheckoutBtn');
if (btn) {
  // FORCE BASELINE (important)
  btn.hidden = true;

  const total = st.totals.g;
  const limit = st.allow.g;

  if (limit != null && total === limit) {
    btn.hidden = false;
  }
}

  setTimeout(()=>{
    setFace(stateToFace(st));
    msgEl.innerHTML = buildMessage(st);
  }, 150);
});

  // Re-run emoji animation every 5s
  clearInterval(animTimer); animTimer = setInterval(replayAnim, 5000);

  // Ensure OL is initialised (derive tag from #ol-feed or global if present)
  const profileTag = (document.getElementById('ol-feed') && document.getElementById('ol-feed').getAttribute('data-profile')) || (window.OL_PROFILE||'');
  OL.init({ profileTag: (profileTag||'').trim() });

  // Clean up on section unload (Shopify editor navigation)
  document.addEventListener('shopify:section:unload', function(){ try{ unsubscribe&&unsubscribe(); clearInterval(animTimer); }catch(_){} });
});
